import React from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchMockVerification, fetchVerification } from '../../actions';
import { ENV } from '../../constants';
import { useVerificationActions } from '../../providers/VerificationProvider';
import type { VerificationAccessFailure } from '../../utils/verificationCredentials';
import {
  VerificationCacheData,
  verificationCacheDataFromSlice,
  verificationQueryKey,
} from './verificationCache';

export { verificationQueryKey } from './verificationCache';

// ECDEX-3840 (slice 4, MR 1): the fetch lifecycle of a verification (loading,
// failed, why it failed) as a query. The fetch itself is still the existing
// thunk, so it keeps writing the `verifications` slice for the readers that
// have not moved yet (the OCR hooks, analytics, the credentials helper).
// MR 2b: the verification, its steps and whether it has expired are the
// query's data too, and are what the page and the verification components read.
// A submit or an unsubscribe copies its result into that data (`syncFromStore`);
// when the thunks stop writing the slice they will hand the result over
// directly.
export class VerificationFetchError extends Error {
  access: VerificationAccessFailure | null;

  constructor(access: VerificationAccessFailure | null) {
    super('verification fetch failed');
    this.access = access;
  }
}

// `?mock=driver_license` in development shows a fixture instead of fetching.
const mockFixture = (): 'driver_license' | 'driver_license_history' | null => {
  if (ENV !== 'development') return null;
  const search = globalThis.location?.search ?? '';
  if (/mock=driver_license_history/.test(search)) {
    return 'driver_license_history';
  }
  if (/mock=driver_license/.test(search)) return 'driver_license';
  return null;
};

const useVerification = (verificationId?: string) => {
  const dispatch = useDispatch<any>();
  const store = useStore<any>();
  const queryClient = useQueryClient();
  const { setVerificationId, setCurrentPage } = useVerificationActions();

  // The provider shows the verification of the page that is asking.
  React.useEffect(() => {
    setVerificationId(verificationId);
  }, [setVerificationId, verificationId]);

  const { data, refetch, isFetching, error } = useQuery<
    VerificationCacheData | null,
    VerificationFetchError
  >({
    queryKey: verificationQueryKey(verificationId),
    // Fetched when the page decides to (see `fetch`), not on mount.
    enabled: false,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      try {
        await dispatch(fetchVerification(verificationId));
      } catch {
        // The thunk has already put its outcome in the store.
      }
      const slice = store.getState().verifications;
      if (slice.errors) throw new VerificationFetchError(slice.access ?? null);
      // A loaded verification starts on its first step (the navigation reducer
      // did this on FETCH_VERIFICATION_SUCCESS).
      setCurrentPage(0);
      return verificationCacheDataFromSlice(slice);
    },
  });

  // The thunk marks the store as fetching at once; the query reports it a tick
  // later, and the page must not flash "not found" in between.
  const sliceFetching = useSelector(
    (state: any) => state.verifications.fetching,
  );

  const fetch = React.useCallback(() => {
    // The fixture stores its verification a moment later and does not set
    // `fetching`, so it bypasses the query: running it as one would flip
    // `fetching` on and off and the page would fetch again, for ever, since the
    // fixture's id is not the page's.
    const fixture = mockFixture();
    if (fixture) {
      dispatch(fetchMockVerification(fixture)(verificationId));
      // The fixture reaches the store a moment later; copy it over then.
      const stopWatching = store.subscribe(() => {
        const next = verificationCacheDataFromSlice(
          store.getState().verifications,
        );
        if (!next) return;
        stopWatching();
        setCurrentPage(0);
        queryClient.setQueryData(verificationQueryKey(verificationId), next);
      });
      return;
    }
    // Not cancelRefetch: a fetch already in flight is the one to wait for.
    refetch({ cancelRefetch: false });
  }, [dispatch, queryClient, refetch, setCurrentPage, store, verificationId]);

  // After a submit or an unsubscribe: put the verification the thunk stored
  // into the data, and keep the steps and expiry that came with the fetch.
  const syncFromStore = React.useCallback(() => {
    const next = verificationCacheDataFromSlice(store.getState().verifications);
    if (!next) return;
    queryClient.setQueryData<VerificationCacheData | null>(
      verificationQueryKey(verificationId),
      previous =>
        previous ? { ...previous, verification: next.verification } : next,
    );
  }, [queryClient, store, verificationId]);

  return {
    // A failed fetch leaves the earlier data in the cache, but the page must
    // not treat that verification as loaded (the old slice cleared it).
    verification: error ? null : data?.verification ?? null,
    fetching: Boolean(isFetching || sliceFetching),
    errors: error ?? null,
    access: error?.access ?? null,
    fetch,
    syncFromStore,
  };
};

export default useVerification;
